const MAX_SOURCE_IMAGE_BYTES = 2 * 1024 * 1024;
const MAX_IMAGE_DATA_URI_LENGTH = 128 * 1024;
// Keep more detail from logos with small lettering while the data URI limit
// below still prevents oversized profile payloads.
const MAX_IMAGE_DIMENSION = 512;

const readFileAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("Unable to read image"));
    reader.readAsDataURL(file);
});

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("Unable to decode image"));
    image.src = src;
});

const renderGrayscalePng = (image, maxDimension) => {
    const ratio = Math.min(1, maxDimension / Math.max(image.naturalWidth, image.naturalHeight));
    const width = Math.max(1, Math.round(image.naturalWidth * ratio));
    const height = Math.max(1, Math.round(image.naturalHeight * ratio));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("Image conversion is not supported");

    context.fillStyle = "#ffffff";
    context.fillRect(0, 0, width, height);
    context.drawImage(image, 0, 0, width, height);

    const pixels = context.getImageData(0, 0, width, height);
    for (let index = 0; index < pixels.data.length; index += 4) {
        const gray = Math.round(
            (0.299 * pixels.data[index])
            + (0.587 * pixels.data[index + 1])
            + (0.114 * pixels.data[index + 2])
        );
        pixels.data[index] = gray;
        pixels.data[index + 1] = gray;
        pixels.data[index + 2] = gray;
        pixels.data[index + 3] = 255;
    }
    context.putImageData(pixels, 0, 0);

    return {
        data: canvas.toDataURL("image/png"),
        width,
        height,
    };
};

export const prepareBarcodeImage = async (file) => {
    if (!file || !file.type?.startsWith("image/")) {
        throw new Error("Please select an image file");
    }
    if (file.size > MAX_SOURCE_IMAGE_BYTES) {
        throw new Error("Image file must be 2 MB or smaller");
    }

    const source = await readFileAsDataUrl(file);
    const image = await loadImage(source);
    let maxDimension = MAX_IMAGE_DIMENSION;
    let rendered;

    for (let attempt = 0; attempt < 4; attempt += 1) {
        rendered = renderGrayscalePng(image, maxDimension);
        if (rendered.data.length <= MAX_IMAGE_DATA_URI_LENGTH) break;
        maxDimension = Math.floor(maxDimension * 0.8);
    }

    if (!rendered || rendered.data.length > MAX_IMAGE_DATA_URI_LENGTH) {
        throw new Error("Image is too detailed. Please choose a smaller image");
    }

    return {
        data: rendered.data,
        mime_type: "image/png",
        format: "PNG",
        width: rendered.width,
        height: rendered.height,
        position: { x: 1, y: 1 },
        size: { width: 8, height: 8 },
    };
};

export const isValidBarcodeImageConfig = (imageConfig) => Boolean(
    imageConfig?.data
    && typeof imageConfig.data === "string"
    && imageConfig.data.startsWith("data:image/png;base64,")
);
